/**
 * O pagina de PDF, transformata in imagine.
 *
 * Pana acum bonurile PDF se afisau intr-un `<iframe>`. Pe desktop merge, fiindca browserul are
 * cititor de PDF incorporat. Pe Android NU: Chrome trateaza PDF-ul dintr-un iframe ca fisier de
 * descarcat si arata o pictograma cu „Deschide" in loc de bon. Adica exact pe telefon, unde se
 * verifica deconturile in mers, bonul nu se vedea deloc.
 *
 * Randand pagina ca imagine, castigam si altceva: zoom-ul, rotirea si deplasarea cu degetul
 * existau deja pentru poze si erau oprite explicit pentru PDF-uri. Acum merg si acolo.
 *
 * `pdfjs-dist` se incarca LENES, la prima pagina ceruta. E o bucata mare de cod si n-are ce
 * cauta in pachetul principal, pe care il descarca toata lumea, pentru un ecran folosit de
 * cativa oameni.
 */

export interface PaginaRandata {
  /** Imaginea paginii, ca `data:` URL. */
  imagine: string
  nrPagini: number
}

/** Peste atat nu se mai castiga lizibilitate, doar memorie consumata. */
const LATIME_MAX = 2000

export async function randeazaPaginaPdf(urlPdf: string, pagina: number): Promise<PaginaRandata> {
  const pdfjs = await import('pdfjs-dist')
  /*
    `workerPort` cu un Worker de tip `module`, NU `workerSrc` cu un URL.

    Workerul lui pdf.js 4 e un modul ES. Cand i se da doar adresa, biblioteca il porneste ca
    worker clasic, pornirea esueaza, si cade tacut pe „fake worker" — adica randeaza pe firul
    principal. Pe un bon scanat de 3000x4000 asta inseamna interfata blocata secunde bune si,
    in practica, nicio imagine.
  */
  if (!pdfjs.GlobalWorkerOptions.workerPort) {
    pdfjs.GlobalWorkerOptions.workerPort = new Worker(
      new URL('pdfjs-dist/build/pdf.worker.min.mjs', import.meta.url),
      { type: 'module' },
    )
  }

  const doc = await pdfjs.getDocument({ url: urlPdf }).promise
  try {
    const nrPagini = doc.numPages
    const p = await doc.getPage(Math.min(Math.max(1, pagina), nrPagini))

    const initial = p.getViewport({ scale: 1 })
    // Bonurile fiscale sunt scrise marunt: se randeaza mai mare decat se afiseaza, ca sa ramana
    // citibile dupa zoom. Plafonat, ca sa nu ajungem la pagini de zeci de megaocteti.
    const scara = Math.min(LATIME_MAX / initial.width, 3)
    const viewport = p.getViewport({ scale: scara })

    const canvas = document.createElement('canvas')
    canvas.width = Math.ceil(viewport.width)
    canvas.height = Math.ceil(viewport.height)
    const ctx = canvas.getContext('2d')
    if (!ctx) throw new Error('Canvas indisponibil')

    await p.render({ canvasContext: ctx, viewport }).promise
    return { imagine: canvas.toDataURL('image/png'), nrPagini }
  } finally {
    void doc.destroy()
  }
}
